/**
 * ProPilote - Pre-cache critical driver data at app startup.
 *
 * Goal: when the chauffeur opens the app online, we silently fetch and store
 * the most useful endpoints in IndexedDB. Then when offline, the app can
 * still navigate (open recent reports, see assignment, etc.).
 *
 * Called once after authentication.
 */
import axios from 'axios';
import { cachePut } from './offlineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENDPOINTS_TO_PREFETCH = [
  '/daily-reports',
  '/driver/assignment/current',
  '/driver/assignment/fleet',
  '/driver/vehicle',
  '/inspections',
  '/service-hours/cycle',
];

const fetchAndCache = async (endpoint, headers) => {
  try {
    const r = await axios.get(`${API_URL}/api${endpoint}`, { headers, timeout: 8000 });
    await cachePut(`get:${endpoint}:{}`, r.data);
    return r.data;
  } catch {
    return null;
  }
};

export const prefetchDriverData = async () => {
  if (!navigator.onLine) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  const headers = { Authorization: `Bearer ${token}` };

  // Fire all main endpoints in parallel
  const reportsList = await fetchAndCache('/daily-reports', headers);

  await Promise.allSettled(
    ENDPOINTS_TO_PREFETCH.filter((e) => e !== '/daily-reports').map((e) =>
      fetchAndCache(e, headers),
    ),
  );

  // Also pre-cache the last 5 individual reports (for offline detail view)
  if (Array.isArray(reportsList) && reportsList.length > 0) {
    const recent = reportsList.slice(0, 5);
    await Promise.allSettled(
      recent.map((r) => r?.id && fetchAndCache(`/daily-reports/${r.id}`, headers)),
    );
  }
};

export default prefetchDriverData;
